import * as yup from "yup";
import {
  BLUE_LOADING_BUTTON,
  BUTTON,
  classes,
  RED_BUTTON,
} from "portal/theme/theme";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  LinearProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import { capitalize, titleCase } from "portal/utils/strings";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import {
  ChecklistAutomationCode,
  checklistAutomationCodeToJSON,
  ChecklistItemDefinition,
} from "protos/rtc/jobs";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { Field, Form, Formik } from "formik";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Header } from "portal/components/header/Header";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Page } from "portal/components/Page";
import { Path } from "portal/utils/routing";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { Select, TextField } from "formik-mui";
import { skipToken } from "@reduxjs/toolkit/query";
import { TFunction } from "i18next";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import SaveIcon from "@mui/icons-material/Save";

const getValidAutomationCodes = (): ChecklistAutomationCode[] => {
  return Object.values(ChecklistAutomationCode)
    .filter((v): v is ChecklistAutomationCode => typeof v === "number")
    .filter((v) => v !== ChecklistAutomationCode.UNRECOGNIZED)
    .filter((v) => v !== ChecklistAutomationCode.AUTOMATION_CODE_UNSPECIFIED);
};

const formatAutomationCodeLabel = (
  code: ChecklistAutomationCode,
  t: TFunction
): string => {
  if (
    code === ChecklistAutomationCode.AUTOMATION_CODE_UNSPECIFIED ||
    code === ChecklistAutomationCode.UNRECOGNIZED
  ) {
    return "";
  }
  if (code === ChecklistAutomationCode.AUTOMATION_CODE_NONE) {
    return t("utils.descriptors.none");
  }
  const enumString = checklistAutomationCodeToJSON(code);
  return t(`models.autotractor.checklists.fields.${enumString}`);
};

const getRowId = (item: ChecklistItemDefinition): number => {
  return item.id;
};

const checklistValidationSchema = yup.object({
  name: yup.string().required(),
  description: yup.string(),
});

const checklistItemValidationSchema = yup.object({
  label: yup.string().required(),
  translationKey: yup.string(),
  automationCode: yup.number().oneOf(getValidAutomationCodes()).required(),
});

interface EditChecklistItemFormProps {
  initialLabel: string;
  initialTranslationKey: string;
  initialAutomationCode: ChecklistAutomationCode;
  onSubmit: (values: {
    label: string;
    translationKey: string;
    automationCode: ChecklistAutomationCode;
  }) => Promise<void>;
  onCancel: () => void;
  onDelete: () => void;
  isLoading: boolean;
}

const EditChecklistItemForm: FunctionComponent<EditChecklistItemFormProps> = ({
  initialLabel,
  initialTranslationKey,
  initialAutomationCode,
  onSubmit,
  onCancel,
  onDelete,
  isLoading,
}) => {
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={{
        label: initialLabel,
        translationKey: initialTranslationKey || "",
        automationCode: initialAutomationCode,
      }}
      validationSchema={checklistItemValidationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        await onSubmit(values);
        setSubmitting(false);
      }}
    >
      {({ submitForm, handleReset, isSubmitting, isValid, dirty }) => (
        <Form className="flex flex-col gap-4">
          <div className="flex justify-end gap-2">
            <Button
              {...RED_BUTTON}
              onClick={onDelete}
              startIcon={<DeleteIcon />}
            >
              {capitalize(t("utils.actions.delete"))}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                onCancel();
                handleReset();
              }}
              className="text-white"
            >
              {capitalize(t("utils.actions.cancel"))}
            </Button>
            <Button
              {...BLUE_LOADING_BUTTON}
              disabled={!isValid || isSubmitting || !dirty}
              loading={isLoading}
              onClick={submitForm}
              startIcon={<SaveIcon />}
              type="submit"
            >
              {capitalize(t("utils.actions.update"))}
            </Button>
          </div>
          <Field
            component={TextField}
            name="label"
            type="text"
            label={capitalize(t("models.autotractor.checklists.fields.label"))}
            variant="standard"
            fullWidth
          />
          <Field
            component={TextField}
            name="translationKey"
            type="text"
            label={capitalize(
              t("models.autotractor.checklists.fields.translation_key")
            )}
            variant="standard"
            fullWidth
          />
          <Field
            component={Select}
            name="automationCode"
            label={capitalize(
              t("models.autotractor.checklists.fields.automation_code")
            )}
            variant="standard"
            fullWidth
          >
            {getValidAutomationCodes().map((v) => (
              <MenuItem key={v} value={v}>
                {formatAutomationCodeLabel(v, t)}
              </MenuItem>
            ))}
          </Field>
        </Form>
      )}
    </Formik>
  );
};

interface EditChecklistFormProps {
  initialName: string;
  initialDescription: string;
  onSubmit: (values: { name: string; description: string }) => Promise<void>;
  onCancel: () => void;
}

const EditChecklistForm: FunctionComponent<EditChecklistFormProps> = ({
  initialName,
  initialDescription,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 p-4">
      <Formik
        initialValues={{ name: initialName, description: initialDescription }}
        validationSchema={checklistValidationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          await onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ submitForm, handleReset, isSubmitting, isValid, dirty }) => (
          <Form className="flex flex-col gap-4">
            <Field
              component={TextField}
              name="name"
              type="text"
              label={capitalize(t("utils.descriptors.name"))}
              variant="standard"
              fullWidth
            />
            <Field
              component={TextField}
              name="description"
              type="text"
              label={capitalize(t("utils.descriptors.description_one"))}
              variant="standard"
              fullWidth
            />
            <div className="flex">
              <Button
                fullWidth
                variant="contained"
                disabled={!isValid || isSubmitting || !dirty}
                type="submit"
                onClick={submitForm}
              >
                {t("utils.actions.save")}
              </Button>
              <Button
                fullWidth
                color="info"
                onClick={() => {
                  onCancel();
                  handleReset();
                }}
              >
                {t("utils.actions.cancel")}
              </Button>
            </div>
            {isSubmitting && (
              <LinearProgress className="w-full" variant="indeterminate" />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

function useEditChecklistDialog(params: {
  checklistDefinitionId: string;
  initialName?: string;
  initialDescription?: string;
}): {
  renderDialog: () => ReactNode;
  open: () => void;
  close: () => void;
} {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [updateChecklist] = useMutationPopups(
    rtcJobsApi.endpoints.updateChecklistDefinition.useMutation()
  );
  const { t } = useTranslation();

  const handleUpdateChecklist = useCallback(
    async (values: { name: string; description: string }): Promise<void> => {
      const result = await updateChecklist({
        id: params.checklistDefinitionId,
        ...values,
      });
      if (!result.error) {
        setIsOpen(false);
      }
    },
    [params.checklistDefinitionId, updateChecklist]
  );

  const handleCancelChecklist = useCallback((): void => {
    setIsOpen(false);
  }, []);

  return {
    renderDialog: () => (
      <Dialog open={isOpen} fullWidth>
        <DialogTitle>
          {titleCase(
            t("utils.actions.editLong", {
              subject: t("models.autotractor.checklists.checklist_one"),
            })
          )}
        </DialogTitle>
        <DialogContent>
          <EditChecklistForm
            initialName={params.initialName || ""}
            initialDescription={params.initialDescription || ""}
            onCancel={handleCancelChecklist}
            onSubmit={handleUpdateChecklist}
          />
        </DialogContent>
      </Dialog>
    ),
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}

interface NewChecklistItemFormProps {
  onSubmit: (values: {
    label: string;
    translationKey: string;
    automationCode: ChecklistAutomationCode;
  }) => Promise<void>;
  onCancel: () => void;
}

const NewChecklistItemForm: FunctionComponent<NewChecklistItemFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 p-4">
      <Formik
        initialValues={{
          label: "",
          translationKey: "",
          automationCode: ChecklistAutomationCode.AUTOMATION_CODE_NONE,
        }}
        validationSchema={checklistItemValidationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          await onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ submitForm, handleReset, isSubmitting, isValid, dirty }) => (
          <Form className="flex flex-col gap-4">
            <Field
              component={TextField}
              name="label"
              type="text"
              label={capitalize(
                t("models.autotractor.checklists.fields.label")
              )}
              fullWidth
            />
            <Field
              component={TextField}
              name="translationKey"
              type="text"
              label={capitalize(
                t("models.autotractor.checklists.fields.translation_key")
              )}
              fullWidth
            />
            <Field
              component={Select}
              name="automationCode"
              label={capitalize(
                t("models.autotractor.checklists.fields.automation_code")
              )}
              variant="standard"
              fullWidth
            >
              {getValidAutomationCodes().map((v) => (
                <MenuItem key={v} value={v}>
                  {formatAutomationCodeLabel(v, t)}
                </MenuItem>
              ))}
            </Field>
            <div className="flex">
              <Button
                fullWidth
                variant="contained"
                disabled={!isValid || isSubmitting || !dirty}
                type="submit"
                onClick={submitForm}
              >
                {t("utils.actions.create")}
              </Button>
              <Button
                fullWidth
                color="info"
                onClick={() => {
                  onCancel();
                  handleReset();
                }}
              >
                {t("utils.actions.cancel")}
              </Button>
            </div>
            {isSubmitting && (
              <LinearProgress className="w-full" variant="indeterminate" />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

function useNewChecklistItemDialog(
  checklistDefinitionId: string,
  existingItems: ChecklistItemDefinition[]
): {
  renderDialog: () => ReactNode;
  open: () => void;
  close: () => void;
} {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [createItem] = useMutationPopups(
    rtcJobsApi.endpoints.createChecklistItemDefinition.useMutation()
  );
  const { t } = useTranslation();

  const handleCreateItem = useCallback(
    async (values: {
      label: string;
      translationKey: string;
      automationCode: ChecklistAutomationCode;
    }): Promise<void> => {
      let maxPosition = 0;
      for (const item of existingItems) {
        const position = item.content?.position ?? 0;
        if (position > maxPosition) {
          maxPosition = position;
        }
      }
      const nextPosition = Math.ceil(maxPosition) + 1;

      const result = await createItem({
        checklistDefinitionId,
        label: values.label,
        position: nextPosition,
        ...(values.translationKey.trim().length > 0
          ? { translationKey: values.translationKey }
          : {}),
        ...(Number(values.automationCode) ===
        ChecklistAutomationCode.AUTOMATION_CODE_NONE
          ? {}
          : { automationCode: Number(values.automationCode) }),
      });
      if (!result.error) {
        setIsOpen(false);
      }
    },
    [checklistDefinitionId, createItem, existingItems]
  );

  const handleCancelItem = useCallback((): void => {
    setIsOpen(false);
  }, []);

  return {
    renderDialog: () => (
      <Dialog open={isOpen} fullWidth>
        <DialogTitle>
          {titleCase(
            t("utils.actions.newLong", {
              subject: t("models.autotractor.checklists.checklist_item_one"),
            })
          )}
        </DialogTitle>
        <DialogContent>
          <NewChecklistItemForm
            onCancel={handleCancelItem}
            onSubmit={handleCreateItem}
          />
        </DialogContent>
      </Dialog>
    ),
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}

export const StartupChecklistDetailsPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const { checklistDefinitionId } = useParams();
  const navigate = useNavigate();

  const editButtonStyle = {
    ...BUTTON,
    className: classes(BUTTON.className, "ml-4"),
  };

  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();

  const {
    data: checklistDefinition,
    isLoading,
    isError,
  } = rtcJobsApi.endpoints.getChecklistDefinition.useQuery(
    checklistDefinitionId ? { id: checklistDefinitionId } : skipToken
  );

  const { renderDialog: renderNewItemDialog, open: openNewItemDialog } =
    useNewChecklistItemDialog(
      checklistDefinitionId ?? "",
      checklistDefinition?.items ?? []
    );

  const { renderDialog: renderEditChecklistDialog, open: openEditChecklist } =
    useEditChecklistDialog({
      checklistDefinitionId: checklistDefinitionId ?? "",
      initialName: checklistDefinition?.content?.name,
      initialDescription: checklistDefinition?.content?.description,
    });

  const selectedItem = useMemo(() => {
    if (!selectedItemId) {
      return;
    }
    return checklistDefinition?.items.find(
      (i) => i.id.toString() === selectedItemId
    );
  }, [checklistDefinition?.items, selectedItemId]);

  const [updateItem, { isLoading: updateItemLoading }] = useMutationPopups(
    rtcJobsApi.endpoints.updateChecklistItemDefinition.useMutation()
  );

  const [deleteItem] = useMutationPopups(
    rtcJobsApi.endpoints.deleteChecklistItemDefinition.useMutation()
  );

  const [confirmDeleteItem, setConfirmDeleteItem] = useState<boolean>(false);

  const [deleteChecklist] = useMutationPopups(
    rtcJobsApi.endpoints.deleteChecklistDefinition.useMutation()
  );
  const [confirmDeleteChecklist, setConfirmDeleteChecklist] =
    useState<boolean>(false);

  const handleCloseItemEditor = useCallback((): void => {
    setSelectedItemId(undefined);
  }, []);

  const handleUpdateItem = useCallback(
    async (values: {
      label: string;
      translationKey: string;
      automationCode: ChecklistAutomationCode;
    }): Promise<void> => {
      if (!checklistDefinitionId || !selectedItem) {
        return;
      }
      const result = await updateItem({
        checklistDefinitionId,
        id: selectedItem.id.toString(),
        label: values.label,
        ...(values.translationKey.trim().length > 0
          ? { translationKey: values.translationKey }
          : {}),
        automationCode: Number(values.automationCode),
      });
      if (!result.error) {
        setSelectedItemId(undefined);
      }
    },
    [checklistDefinitionId, selectedItem, updateItem]
  );

  const handleDeleteItem = useCallback((): void => {
    setConfirmDeleteItem(true);
  }, []);

  const rows = useMemo(() => {
    return checklistDefinition?.items ?? [];
  }, [checklistDefinition]);

  const columns = useMemo<GridColDef<ChecklistItemDefinition>[]>(() => {
    const defaultColumn: Partial<GridColDef> = {
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
    };
    return [
      {
        ...defaultColumn,
        field: "checkbox",
        headerName: "",
        flex: 0,
        width: 50,
        sortable: false,
        display: "flex",
        renderCell: () => (
          <Checkbox
            checked
            disabled
            size="small"
            sx={{ "&.Mui-disabled": { color: "text.secondary" } }}
          />
        ),
      },
      {
        ...defaultColumn,
        field: "label",
        headerName: capitalize(t("models.autotractor.checklists.fields.label")),
        flex: 2,
        valueGetter: (value, row) => row.content?.label ?? "",
      },
      {
        ...defaultColumn,
        field: "translationKey",
        headerName: capitalize(
          t("models.autotractor.checklists.fields.translation_key")
        ),
        valueGetter: (value, row) => row.content?.translationKey ?? "",
      },
      {
        ...defaultColumn,
        field: "automationCode",
        headerName: capitalize(
          t("models.autotractor.checklists.fields.automation_code")
        ),
        valueGetter: (value, row) => {
          const code = row.content?.automationCode;
          if (
            code === ChecklistAutomationCode.AUTOMATION_CODE_NONE ||
            code === ChecklistAutomationCode.AUTOMATION_CODE_UNSPECIFIED ||
            code === ChecklistAutomationCode.UNRECOGNIZED ||
            code === undefined
          ) {
            return "";
          }
          return formatAutomationCodeLabel(code, t);
        },
      },
    ];
  }, [t]);

  if (isError || !checklistDefinitionId) {
    return <Navigate to={Path.STARTUP_CHECKLISTS} />;
  }

  return (
    <>
      {renderNewItemDialog()}
      {renderEditChecklistDialog()}
      <Header
        title={capitalize(
          t("models.autotractor.checklists.startup_checklist_other")
        )}
        parentLink={Path.STARTUP_CHECKLISTS}
      >
        <Button
          className="text-white ml-auto"
          startIcon={<AddIcon />}
          onClick={openNewItemDialog}
        >
          <span className="hidden sm:inline">
            {titleCase(
              t("utils.actions.newLong", {
                subject: t("models.autotractor.checklists.checklist_item_one"),
              })
            )}
          </span>
          <span className="sm:hidden">{t("utils.actions.new")}</span>
        </Button>
      </Header>
      <Page>
        <div className="px-4 py-3">
          <div className="flex items-center flex-wrap gap-4">
            <Typography variant="h1" className="text-4xl">
              {checklistDefinition?.content?.name ??
                t("models.autotractor.checklists.startup_checklist_one")}
            </Typography>
            <Button
              {...editButtonStyle}
              startIcon={<EditIcon />}
              onClick={openEditChecklist}
            >
              {t("utils.actions.edit")}
            </Button>
            <Button
              {...RED_BUTTON}
              onClick={() => setConfirmDeleteChecklist(true)}
              startIcon={<DeleteIcon />}
            >
              {t("utils.actions.delete")}
            </Button>
            {confirmDeleteChecklist && checklistDefinitionId && (
              <ConfirmationDialog
                title={t("utils.actions.deleteLong", {
                  subject: t(
                    "models.autotractor.checklists.startup_checklist_one"
                  ),
                })}
                description={t(
                  "components.ConfirmationDialog.delete.description",
                  {
                    subject: checklistDefinition?.content?.name ?? "",
                  }
                )}
                destructive
                yesText={t("utils.actions.delete")}
                onClose={() => setConfirmDeleteChecklist(false)}
                onYes={async () => {
                  await deleteChecklist({ id: checklistDefinitionId });
                  setConfirmDeleteChecklist(false);
                  navigate(Path.STARTUP_CHECKLISTS);
                }}
              />
            )}
          </div>
        </div>
        <div className="flex flex-col h-full">
          {isLoading && <CircularProgress variant="indeterminate" />}
          {checklistDefinition && (
            <div className="flex-grow">
              <CarbonDataGrid<ChecklistItemDefinition>
                dimensionClasses={classes({
                  "mr-80": Boolean(selectedItem),
                })}
                columns={columns}
                rows={rows}
                loading={isLoading}
                getRowId={getRowId}
                getRowClassName={({ row }) =>
                  classes("cursor-pointer", {
                    "bg-blue-500": row.id.toString() === selectedItemId,
                  })
                }
                onRowClick={({ row }) => setSelectedItemId(row.id.toString())}
                disableRowSelectionOnClick
                hideFooter
              />
            </div>
          )}
          <Drawer
            variant="permanent"
            anchor="right"
            open={Boolean(selectedItem)}
            classes={{
              paper: classes(
                "h-auto p-4 bottom-0 flex flex-col gap-4",
                "top-0 md:top-16",
                "w-screen md:w-80",
                {
                  hidden: !selectedItem,
                }
              ),
            }}
          >
            {selectedItem && (
              <>
                <EditChecklistItemForm
                  key={selectedItem.id}
                  initialLabel={selectedItem.content?.label || ""}
                  initialTranslationKey={
                    selectedItem.content?.translationKey || ""
                  }
                  initialAutomationCode={
                    selectedItem.content?.automationCode ||
                    ChecklistAutomationCode.AUTOMATION_CODE_NONE
                  }
                  onSubmit={handleUpdateItem}
                  onCancel={handleCloseItemEditor}
                  onDelete={handleDeleteItem}
                  isLoading={updateItemLoading}
                />
                {confirmDeleteItem && checklistDefinitionId && (
                  <ConfirmationDialog
                    title={t("utils.actions.deleteLong", {
                      subject: t(
                        "models.autotractor.checklists.checklist_item_one"
                      ),
                    })}
                    description={t(
                      "components.ConfirmationDialog.delete.description",
                      {
                        subject: capitalize(
                          t("models.autotractor.checklists.checklist_item_one")
                        ),
                      }
                    )}
                    destructive
                    yesText={t("utils.actions.delete")}
                    onClose={() => setConfirmDeleteItem(false)}
                    onYes={async () => {
                      await deleteItem({
                        checklistDefinitionId,
                        id: selectedItem.id.toString(),
                      });
                      setConfirmDeleteItem(false);
                      setSelectedItemId(undefined);
                    }}
                  />
                )}
              </>
            )}
          </Drawer>
        </div>
      </Page>
    </>
  );
};
